import { LoadLocation, LoadTimeWindow } from '@numeo/types-core'
import { format } from 'date-fns'

const BUSINESS_ENTITY_SUFFIXES = ['LLC', 'INC', 'LTD', 'CO', 'CORP', 'PLC', 'LLP', 'LP', 'PC', 'PLLC', 'S.A.', 'S.A.S.', 'AG', 'KG', 'GMBH']

/**
 * Standardize text for consistent formatting across services.
 * Handles proper casing while preserving business entity suffixes (LLC, INC, etc.)
 */
export function standardizeText(text: string): string {
    if (!text) return ''

    const exceptions = new Set(BUSINESS_ENTITY_SUFFIXES)

    return text
        .split(' ')
        .map((word) => {
            // Remove punctuation for matching, but keep original for output
            const cleanWord = word.replace(/[^a-zA-Z.]/g, '').toUpperCase()
            if (exceptions.has(cleanWord)) {
                return word
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join(' ')
}

/**
 * Convert camelCase string to human readable format
 * @param key String to convert (e.g., 'driverShouldLoad')
 * @returns Human readable string (e.g., 'Driver should load')
 */
export const formatFieldName = (key: string): string => {
    // Handle empty or falsy keys
    if (!key) return key

    // Add space before capital letters and trim
    const withSpaces = key.replace(/([A-Z])/g, ' $1').trim()

    // Capitalize first letter and return
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}

/**
 * Format field value to be more human readable
 * @param value Value to format
 * @returns Formatted string
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatFieldValue = (value: any, keyName: string): string => {
    if (value === null || value === undefined) return '—'

    // Handle boolean values
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'

    // Handle numbers first to avoid date parsing
    if (typeof value === 'number') {
        return value.toLocaleString()
    }

    // Handle string values
    if (typeof value === 'string') {
        // Handle numeric strings (like weight)
        if (/^\d+$/.test(value)) {
            // Format as number with thousands separators
            return parseInt(value, 10).toLocaleString()
        }

        // Handle date strings that are already in a nice format
        const lowerValue = value.toLowerCase()
        if (lowerValue.includes('today') || lowerValue.includes('tomorrow') || lowerValue.match(/^(mon|tue|wed|thu|fri|sat|sun)day/i)) {
            return value
        }

        // Try to parse as date if it looks like a date string
        try {
            const date = new Date(value)
            if (!isNaN(date.getTime())) {
                return format(date, 'MMM d, yyyy h:mm a')
            }
        } catch {
            // If date parsing fails, continue to string handling
        }

        // Capitalize first letter for regular strings
        return value.charAt(0).toUpperCase() + value.slice(1)
    }

    // Handle Date objects
    if (value instanceof Date) {
        return format(value, 'MMM d, yyyy h:mm a')
    }

    if (['pickupDateTime', 'deliveryDateTime'].includes(keyName)) {
        if (Array.isArray(value)) {
            return value
                .map((date: LoadTimeWindow, index) => {
                    let result = ''
                    if (value.length > 1) {
                        result += keyName === 'pickupDateTime' ? `Pickup #${index + 1}: ` : `Delivery #${index + 1}: `
                    }

                    if (date.scheduledAt) result += ` ${date.scheduledAt}`
                    if (date.fcfs) result += ` (FCFS)`
                    if (date.appointmentRequired) result += ` (Appointment Required)`

                    return result
                })
                .join(', ')
        } else if (typeof value === 'string') {
            return value
        }
    }

    // For arrays or objects, convert to string
    return JSON.stringify(value)
}

/**
 * Normalize a city name to Title Case. Broker feeds often arrive as ALL CAPS
 * ("CHICAGO") or lowercase ("chicago") — both look noisy in tables and emails.
 * Handles multi-word names, apostrophes, hyphens, slashes. Leaves
 * already-mixed-case strings untouched (assumed correct).
 */
export const formatCity = (city: string | null | undefined): string => {
    if (!city) return ''
    const trimmed = city.trim()
    if (!trimmed) return ''
    const allCaps = trimmed === trimmed.toUpperCase()
    const allLower = trimmed === trimmed.toLowerCase()
    if (!allCaps && !allLower) return trimmed
    return trimmed.toLowerCase().replace(/(^|[\s\-'/])([a-z])/g, (_, sep, ch) => `${sep}${ch.toUpperCase()}`)
}

/**
 * Tokenize a loads-table search query the same way the backend does: split on
 * commas and whitespace, drop empties. Used both in the PG query and for
 * client-side match highlighting so they stay aligned.
 */
export const tokenizeLoadsSearch = (query: string | null | undefined): string[] => {
    if (!query) return []
    return query
        .split(/[,\s]+/)
        .map((t) => t.trim())
        .filter(Boolean)
}

/**
 * Permissive money formatter for values that may arrive as strings from
 * Postgres DECIMAL (node-pg returns those as strings, which silently defeats
 * `toLocaleString` and leaks float artifacts like "1348.1300048828125"). Coerces
 * to number, rounds to whole dollars, adds thousands. Returns the em-dash
 * marker for null/undefined/non-finite inputs so streaming/partial rows render
 * cleanly. Use this when the value source is uncertain (broker feeds,
 * pre-resolved API rows); use `formatCurrency` when you control the number
 * type and want decimal control.
 */
export const formatMoney = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '—'
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return '—'
    return `$${Math.round(n).toLocaleString('en-US')}`
}

/**
 * The shared cut behind the truncating formatters. The value is first pinned to six decimals to drop float
 * noise ("1348.1300048828125" → 1348.13; 0.29 never becomes 0.28). `negative` is false for an amount that
 * truncates to zero, so there is never a "-0.00".
 */
const truncateToCents = (value: number | string | null | undefined): { negative: boolean; whole: string; cents: string } | null => {
    if (value === null || value === undefined || value === '') return null
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return null
    const [whole, fraction = ''] = Math.abs(n).toFixed(6).split('.')
    const cents = fraction.slice(0, 2)
    return { negative: n < 0 && (Number(whole) > 0 || Number(cents) > 0), whole, cents }
}

/**
 * Exact-cents sibling of `formatMoney`: same input contract (number, PG DECIMAL string, null → em-dash),
 * but the value is cut at two decimals, never rounded, so a displayed row can never read higher than the
 * stored amount and rows add up to a total shown the same way ("1348.1300048828125" → "$1,348.13").
 */
export const formatMoneyTruncated = (value: number | string | null | undefined): string => {
    const parts = truncateToCents(value)
    if (!parts) return '—'
    return `${parts.negative ? '-' : ''}$${Number(parts.whole).toLocaleString('en-US')}.${parts.cents}`
}

/**
 * Plain-number counterpart of `formatMoneyTruncated` (hours, counts with fractions): cut at two decimals, never
 * rounded ("12.349" → "12.34"). `signed` puts a "+" on a positive result.
 */
export const formatNumberTruncated = (value: number | string | null | undefined, { signed = false }: { signed?: boolean } = {}): string => {
    const parts = truncateToCents(value)
    if (!parts) return '—'
    const sign = parts.negative ? '-' : signed && (Number(parts.whole) > 0 || Number(parts.cents) > 0) ? '+' : ''
    return `${sign}${Number(parts.whole).toLocaleString('en-US')}.${parts.cents}`
}

/**
 * Percent counterpart of `formatMoneyTruncated`: cut at two decimals, never rounded ("12.349" → "12.34%").
 * `signed` puts a "+" on a positive result, so a change reads "+12.34%" / "-3.10%" / "0.00%".
 */
export const formatPercentTruncated = (value: number | string | null | undefined, { signed = false }: { signed?: boolean } = {}): string => {
    const parts = truncateToCents(value)
    if (!parts) return '—'
    const sign = parts.negative ? '-' : signed && (Number(parts.whole) > 0 || Number(parts.cents) > 0) ? '+' : ''
    return `${sign}${Number(parts.whole)}.${parts.cents}%`
}

/**
 * Format a number as currency (USD)
 * @param value Number to format
 * @param decimals Number of decimal places (default: 0)
 * @returns Formatted currency string
 */
export const formatCurrency = (value: number | null, decimals: number = 0): string => {
    if (value === null) return ''
    // An amount that rounds to zero is shown as zero: never "-$0.00" for a negative zero or a rounding remainder like -0.001.
    const roundsToZero = Number(value.toFixed(decimals)) === 0
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(roundsToZero ? 0 : value)
}

// Map of state abbreviations to full names for the GeoJSON data
export const stateAbbreviationMap: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    DC: 'District of Columbia',
}

// Get state center coordinates for annotations
export const stateCenters: Record<string, number[]> = {
    Alabama: [-86.79113, 32.806671],
    Alaska: [-152.404419, 61.370716],
    Arizona: [-111.431221, 33.729759],
    Arkansas: [-92.373123, 34.969704],
    California: [-119.681564, 36.116203],
    Colorado: [-105.311104, 39.059811],
    Connecticut: [-72.755371, 41.597782],
    Delaware: [-75.507141, 39.318523],
    Florida: [-81.686783, 27.766279],
    Georgia: [-83.643074, 33.040619],
    Hawaii: [-157.498337, 21.094318],
    Idaho: [-114.478828, 44.240459],
    Illinois: [-88.986137, 40.349457],
    Indiana: [-86.258278, 39.849426],
    Iowa: [-93.210526, 42.011539],
    Kansas: [-96.726486, 38.5266],
    Kentucky: [-84.670067, 37.66814],
    Louisiana: [-91.867805, 31.169546],
    Maine: [-69.381927, 44.693947],
    Maryland: [-76.802101, 39.063946],
    Massachusetts: [-71.530106, 42.230171],
    Michigan: [-84.536095, 43.326618],
    Minnesota: [-93.900192, 45.694454],
    Mississippi: [-89.678696, 32.741646],
    Missouri: [-92.288368, 38.456085],
    Montana: [-110.454353, 46.921925],
    Nebraska: [-98.268082, 41.12537],
    Nevada: [-117.055374, 38.313515],
    'New Hampshire': [-71.563896, 43.452492],
    'New Jersey': [-74.521011, 40.298904],
    'New Mexico': [-106.248482, 34.840515],
    'New York': [-74.948051, 42.165726],
    'North Carolina': [-79.806419, 35.630066],
    'North Dakota': [-99.784012, 47.528912],
    Ohio: [-82.764915, 40.388783],
    Oklahoma: [-96.928917, 35.565342],
    Oregon: [-122.070938, 44.572021],
    Pennsylvania: [-77.209755, 40.590752],
    'Rhode Island': [-71.51178, 41.680893],
    'South Carolina': [-80.945007, 33.856892],
    'South Dakota': [-99.438828, 44.299782],
    Tennessee: [-86.692345, 35.747845],
    Texas: [-97.563461, 31.054487],
    Utah: [-111.862434, 40.150032],
    Vermont: [-72.710686, 44.045876],
    Virginia: [-78.169968, 37.769337],
    Washington: [-121.490494, 47.400902],
    'West Virginia': [-80.954453, 38.491226],
    Wisconsin: [-89.616508, 44.268543],
    Wyoming: [-107.30249, 42.755966],
    'District of Columbia': [-77.026817, 38.897438],
}

/**
 * Formats a duration in seconds into a human-readable MM:SS format.
 * Pads both minutes and seconds with leading zeros if needed.
 *
 * @example
 * convertSecondsToMMSS(125) // returns "02:05"
 * convertSecondsToMMSS(5)   // returns "00:05"
 *
 * @param {number} seconds - The duration in seconds to format
 * @returns {string} Formatted time string in MM:SS format
 */
export const convertSecondsToMMSS = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/**
 * Calculates and formats a rate per mile value
 * @param {number} rate - The total rate amount
 * @param {number} miles - The number of miles
 * @returns {string} Formatted string as "$X.XX/mi" or "N/A" if miles is 0 or invalid
 * @example
 * formatRatePerMile(100, 50) // returns "$2.00/mi"
 * formatRatePerMile(100, 0)  // returns "N/A"
 */
export const formatRatePerMile = (rate: number, miles: number): string => {
    if (!miles || miles === 0) return 'N/A'
    const ratePerMile = rate / miles
    return `$${ratePerMile.toFixed(2)}/mi`
}

/**
 * Value-only sibling of `formatRatePerMile`: returns just the numeric string
 * (e.g. "2.00") or `null` when the value should be hidden, instead of the
 * decorated "$X.XX/mi" / "N/A" form. Returns null for non-positive rate or
 * miles (incl. string "0" / "0.00") so streaming rows that arrive with rate=0
 * before the full payload lands don't flash "$0.00" / "0.00" in the RPM
 * column. Caller renders the "$" and "/mi" decorations in JSX.
 *
 * Accepts string inputs because PG DECIMAL arrives as a string via node-pg.
 */
export const formatRatePerMileValue = (rate: number | string | null | undefined, miles: number | string | null | undefined): string | null => {
    if (rate == null || miles == null) return null
    const r = Number(rate)
    const m = Number(miles)
    if (!Number.isFinite(r) || !Number.isFinite(m) || r <= 0 || m <= 0) return null
    return (r / m).toFixed(2)
}

export const formatLoadLocation = (location: LoadLocation): string => {
    if (!location) return 'Unknown'
    const city = location.city || ''
    const state = location.stateProv || ''
    return `${city}${state ? `, ${state}` : ''}`
}
